import { Injectable, OnDestroy } from "@angular/core";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { environment } from "../../../environments/environment";
import { StateService } from "../../@core/utils";
import { NbAuthService } from "../../authentication/services/auth.service";
import { NbTokenService } from "../../authentication/services/token/token.service";
import { availableLanguages, frontendRoleType, searchQueryOptions, SearchRelatedUserOptions, strapiMeta, subscriptionStatus } from "../../interfaces/apiCalls";
import { loginPagePath, noAuthNeededPaths } from "../pagePaths";
//@ts-ignore
const qs = require('qs');
import { compareTypesVersionAndShowWarning, exists, NavigationHelpers } from "../../common/utils";
import { publicAppRoutes, isPublicAppRoute } from "../pagePaths";
import { NbComponentOrCustomStatus, NbComponentStatus, NbGlobalPhysicalPosition, NbGlobalPosition, NbToastrConfig, NbToastrService } from "@nebular/theme";
import { GcloudLocationDetailResponse, GcloudLocationSearchResponse } from "../../pages/forms/address-search/mapsTypes";
import { docTypePlural } from "../../shared-types/datatypes";
import { TranslateService } from "@ngx-translate/core";
import { PrincipalInfo, RegisterOrLoginWithP12 } from "@med_poc/types"
import { UserDetails } from "@med_poc/types/";
import { createProfessionalGroup as import_createProfessionalGroup, myGroupsAndInvites as import_myGroupsAndInvites, 
  inviteToProfessionalGroup as import_inviteToProfessionalGroup, acceptInviteToProfessionalGroup as import_acceptInviteToProfessionalGroup, 
  leaveOrRemoveFromGroup as import_leaveOrRemoveFromGroup, myDirectEmployees as import_myDirectEmployees, deleteRelation as import_deleteRelation, sendProfessionalRegistrationInvite as import_sendProfessionalRegistrationInvite, getMyTeams as import_getMyTeams, inviteEmployee as import_inviteEmployee, getMyEmployeeInvites as import_getMyEmployeeInvites, acceptEmployeeInvite as import_acceptEmployeeInvite, getAllowedPrincipals as import_getAllowedPrincipals }
   from "../api/apiFunctions/groups"

import { getMyFavouriteIcd10Codes as import_getMyFavouriteIcd10Codes, updateMyFavouriteIcd10Codes as import_updateMyFavouriteIcd10Codes } from "./apiFunctions/favouriteIcd10Codes";
import { createProfessionalPaymentAccount as import_createProfessionalPaymentAccount, checkVerificationStatus as import_checkVerificationStatus, registerPaymentCard as import_registerPaymentCard, chargePatient as import_chargePatient, deletePaymentCard as import_deletePaymentCard, setDefaultPaymentCard as import_setDefaultPaymentCard, createPaymentLink as import_createPaymentLink, getMyCards as import_getMyCards, getPaymentLinks as import_getPaymentLinks, resendPaymentLink as import_resendPaymentLink  } from "./apiFunctions/payment";
import { inviteUserWithCode as import_inviteUserWithCode, acceptInviteUserWithCode as import_acceptInviteUserWithCode, getMyUserInvites as import_getMyUserInvites, respondToUserInvite as import_respondToUserInvite, registerUser as import_registerUser } from "./apiFunctions/groupsUsers";
import { findLatestFormSubmission as import_findLatestFormSubmission, loadPdfFormSchema as import_loadPdfFormSchema, saveFormSubmission as import_saveFormSubmission, findPdfForms as import_findPdfForms  } from './apiFunctions/forms';
import { authenticateWithOidc as import_authenticateWithOidc} from "./apiFunctions/esante";
import { Subject } from "rxjs";
import { cloneDeepWith } from "lodash-es";
import { eCit as import_ecit, getOIDCstatusAndExtend as import_getOIDCstatusAndExtend, getMyEcits as import_getMyEcits } from "./apiFunctions/esante";

@Injectable({
  providedIn: "root",
})
export class ApiService implements OnDestroy{

  destroy$: Subject<void> = new Subject<void>();

  private readonly trimExcludedKeyPattern = /(pass(word)?|token|secret|api[-_]?key|authorization|auth)/i;

  /** The current in-flight setMe() call, so isAuthenticated() can await it. Null when idle. */
  private setMeInFlight: Promise<unknown> | null = null;

  private shouldSkipStringTrim(key: unknown): boolean {
    return typeof key === "string" && this.trimExcludedKeyPattern.test(key);
  }

  // Normalize payloads at the API boundary so submit pathways (click/Enter) behave consistently.
  private deepTrimStrings<T>(value: T): T {
    return cloneDeepWith(value, (currentValue, key) => {
      if (typeof currentValue !== "string") return undefined;
      if (this.shouldSkipStringTrim(key)) return currentValue;
      return currentValue.trim();
    },
    ) as T;
  }

  constructor(
    private authService: NbAuthService,
    private tokenService: NbTokenService,
    public state: StateService,
    public router: Router,
    private navigate: NavigationHelpers,
    public toastrService: NbToastrService,
    public translate : TranslateService,
    private location: Location
  ) {


    // request interceptor to handle auth token
    const that = this
    const thePublicAppRoutes = publicAppRoutes
    
    axios.interceptors.request.use(
      async function (config : InternalAxiosRequestConfig<any>) {
        // Check if the request should skip the interceptor
        
        //@ts-ignore
        if (config.skipInterceptor) {
          return config;
        }

        const abortController = new AbortController();

        // if PUT/POST and not a login attempt, check the subscription status and show warning if expired
        if (['post', 'put'].includes(config.method.toLowerCase()) && state.subscriptionStatus === subscriptionStatus.EXPIRED && !config.url.includes("/auth/")) {
          that.showSubscriptionExpired();
          abortController.abort();
          return Promise.reject({
            response: {
              status: 402,
              data: { message: 'subscription expired' }
            },
            isInterceptorRejection: true
          });
        }

        
        // filter URLS where the token jwt isn't necessary
        const reqPath = config.url.replace(environment.apiUrl, "");
        const matchedPublicRoutes = noAuthNeededPaths().filter((path) =>
          reqPath.includes(path)
        );

        var authenticated;
        // Check if token is present and valid
        // Todo: Request is still being sent in this case !
        authService.isAuthenticated().subscribe((isAuthenticated) => {
          authenticated = isAuthenticated;
        });
        //Case1: Authenticated or not, public API
        if (matchedPublicRoutes.length > 0) {
          return config;
        } else if (authenticated && matchedPublicRoutes.length === 0) {
          // Case 2: Authenticated, but private API
          var token;

          authService.getToken().subscribe((authToken) => {
            token = authToken;
          });

          config["headers"]["Authorization"] = `Bearer ${token.token}`;

          return config;
        // } else if (!authenticated && (matchedPublicRoutes.length === 0 && !thePublicAppRoutes.includes(that.router.url))) {
        } else if (
          // !authenticated && (matchedPublicRoutes.length === 0 && !exists(thePublicAppRoutes, that.router.url))
          notAuthenticatedAndNotPublic(authenticated, matchedPublicRoutes)
        ) {
          let includes = !thePublicAppRoutes.includes(that.router.url)
          // Case 3: Not authenticated, private API, not on public app route

          // abort request - check if wokrs as documented
          abortController.abort();
          console.devlog("config", config);

          return Promise.reject({
            response: {
              status: 401,
              data: { message: 'not authenticated' }
            },
            isInterceptorRejection: true
          });
        }

        // Fallback: return config if no condition matched
        return config;
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      }
    );

    function notAuthenticatedAndNotPublic(authenticated, matchedPublicRoutes){
      return !authenticated && !isPublicAppRoute(that.router.url)
    }

    this.isAuthenticated().then((auth) => {
      
      // fetch all roles only if a user is really logged in.
      if(this.state.user && this.state.user.role) {
      //get all existing roles and store them into state
      this.state.loadRoles().catch((error) => {
        console.deverror("Error fetching roles on API service init:", error);
      });
      console.devlog("Fetching professional roles on API service init...");
      this.getAllProfessionalRoles().then((roles : frontendRoleType[]) => {
        this.state.professionalRoles = roles;
      });
    }
    }).catch(error => {
      console.devlog("User not authenticated, not fetching roles.")
    });



  }

  // clear the stored token and the per-user state, then redirect to the login page
  private handleRejectedSession(): void {
    console.devlog("Session rejected by the backend, clearing token and redirecting to login");
    this.tokenService.clear();
    this.state.wipe();
    this.state.clearPinnedUser();
    this.state.clearSelectedPrincipal();
    this.router.navigate([loginPagePath()]);
  }

  /**
   * Show notification(s) for subscription expired (toast, etc..) based on user role
   */
  showSubscriptionExpired() {
    let toastCfg : Partial<NbToastrConfig> = { status: ('warning' as 	NbComponentStatus), position: ('top-right' as NbGlobalPhysicalPosition), duration: 0, /* icon: 'nb-locked',  */toastClass: 'toastClassTest' };
    // if the user's role is 'authenticated', show a generic toast
    if (this.state.user && this.state.user.role && this.state.user.role.type === 'authenticated') {
      this.toastrService.show(this.translate.instant('toasts.readOnlyModeTitle'), this.translate.instant('toasts.readOnlyModeText') , toastCfg);
    } else {
      // if the user's role is 'administrator', show a toast with a link to the subscription page
      toastCfg = { ...toastCfg, status: ('warning' as 	NbComponentStatus), duration: 0, /* icon: 'nb-locked' */ }
      this.toastrService.show(this.translate.instant('toasts.readOnlyModeTextAdmin'), this.translate.instant('toasts.readOnlyModeTitleAdmin'), toastCfg);
    }
   }

  async createNewUser(data: any) {
    return new Promise((resolve, reject) => {
     

      var config = {
        method: 'post',
        url: `${environment.apiUrl}api/users`,
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json'
        },
        data : data
      };

      axios(config)
      .then(function (response) {
        resolve(response.data)
      })
      .catch(function (error) {
        console.devlog("api create user error: ", error);
        reject(error)
      });


    });
  }

  /**
   * Ask the backend for details of logged-in user
   */
  async getUsersMe(opts?: { overridePublicRoutesCheck?: boolean, populate?: string[] }) {
    const that = this
    return new Promise((resolve, reject) => {

      let toPopulate = {
        role: true, //always populate role by default
      }

      // get the populate options from opts and add them to the toPopulate object
      if(opts?.populate && opts.populate.length > 0) {
        opts.populate.forEach((prop) => {
          toPopulate[prop] = true;
        });
      }

      const query = qs.stringify({
        populate: toPopulate
      }, { encodeValuesOnly: true });
  
      var config = {
        method: "get",
        url: `${environment.apiUrl}api/users/me?${query}`,
        headers: {
          Accept: "application/json",
        },
      };

      
      // if we're in a public frontend route, reject the promise and don't redirect to login. But when this is ran explicitly after a login, override 
      // this logic to allow fetching user data even when on public routes (e.g. the login page), so this informatin is available when loading the next page already.
    console.devlog("where are we?", this.router.url)  


// can this be removed? checking for if in public route is done in the calling function already.
    if(!opts?.overridePublicRoutesCheck) {
      if(isPublicAppRoute(that.location.path())) {
        reject("In public app route, not redirecting to login")
        return
      } else {
        publicAppRoutes
        that.router.url

        console.devlog("Not in public app route, rejecting function call so parent can redirect...")
      }
    }
      axios(config)
        .then(async function (response: AxiosResponse) {
          console.devlog(response.data);
          (environment.environmentType !== 'prod') && compareTypesVersionAndShowWarning(response.headers['x-types-version'], that.toastrService, that.translate);
          resolve(response.data);
          })
        .catch(function (error) {
          // if fetching the user details failed, only redirect to login when not on 'auth/login' path or /login?otlt, because that 
          // brings racing conditions with oneTimeLoginToken logins !
          console.devlog('router url', that.router.url)
          // leave one time login token paths alone and give them some time.
          if(that.router.url.trim().startsWith("/login?otlt")){
            console.devlog("Found OTLT, not redirecting..")
            return
          }

          reject(error);
        });
    });
  }
  /**
   * Ask the backend for user details and set them in the state
   */
  async setMe(opts?: { overridePublicRoutesCheck?: boolean }) {
    // Most callers (AppComponent.ngOnInit) fire this without awaiting, so hold on to the
    // in-flight promise: it lets isAuthenticated() await the real fetch instead of polling
    // for a side effect on a timer.
    const run = this._setMe(opts);
    this.setMeInFlight = run.finally(() => {
      if (this.setMeInFlight === run) this.setMeInFlight = null;
    });
    return run;
  }

  private async _setMe(opts?: { overridePublicRoutesCheck?: boolean }) {
    console.devlog("setMe");
// if the user is not logged in and this is a public route, do not attempt to fetch user details.
    if (!opts?.overridePublicRoutesCheck && isPublicAppRoute(this.location.path())) {
      console.devlog("User is not logged in and on a public route, not fetching user details.");
      return;
    }


    try {

      let results = await this.getUsersMe({overridePublicRoutesCheck: opts?.overridePublicRoutesCheck});
      this.state.setUser(results);
      this.state.loadRoles(true).catch((error) => console.deverror("Error refreshing roles:", error)); // also fetch roles, in case something has changed.
    } catch(err){
      console.devlog("Error? ", err)
      // the token looks valid locally but the backend rejected it (e.g. user deleted, db reset, jwt secret changed):
      // drop it and go to login instead of leaving the user in an app shell without a user
      if (err?.response?.status === 401 && !err?.isInterceptorRejection) {
        this.handleRejectedSession();
        return;
      }

    }

    // if it is an authenticated_professional, fetch again immediately to get the `details` property, because the first fetch doesn't include them.
    // unfortunately, strapi errors when sending 'populate' props which do not exist on some record (e.g. a normal user)
    if(this.state.user && this.state.user.role && this.state.user.role.type === 'authenticated_professional' && !this.state.user.details) {
      try {
        let results = await this.getUsersMe({overridePublicRoutesCheck: opts?.overridePublicRoutesCheck , populate: ['details']});
        this.state.setUser(results);
        this.state.loadRoles(true).catch((error) => console.deverror("Error refreshing roles:", error)); // also fetch roles, in case something has changed.
      } catch(err){
        console.devlog("Error? ", err)
      }
    }
  }
  
  
  /**
   * Ask the backend for details of logged-in user
   * @param documentId User documentId
   * @param fetchFromMaster Fetch this record through the child from master. E.g. You <-> child instance <-> master instance
  */
 async getUserById(documentId : string, fetchFromMaster) {
   return new Promise(async (resolve, reject) => {

      /*  if (id == 'me') return await this.getUsersMe();  */
   if (documentId == 'me') { 
    let user = await this.getUsersMe();
    return resolve(user);
   }

  let theUrl = fetchFromMaster ? `${environment.apiUrl}api/users-permissions/findOneFromMaster/${documentId}` : `${environment.apiUrl}api/users/${documentId}?populate=%2A`;   

     var config = {
       method: "get",
       url: theUrl,
       headers: {
         Accept: "application/json",
        },
      };
      
      axios(config)
      .then(function (response: AxiosResponse) {
        console.devlog(response.data);
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      });
    });
  }

  /**
   * Find one related user by documentId 
   * return same data as getUserById
   */

  async findOneRelatedUser(documentId: string){
    return new Promise((resolve, reject)=>{
      const config = {
        method: "get",
        url: `${environment.apiUrl}api/users-permissions/relatedUser/${documentId}`,
        headers: {
          Accept: 'application/json',
        },
      };
      
      axios(config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error)=>{
          console.devlog(error, "error when fetching related user")
          reject(error)
        })
    })
  }

  async findRelatedUsers(sqo: searchQueryOptions, options?: SearchRelatedUserOptions){
    // an array of objects to be searched 
    let filteredProperties = ['username','email', 'matricule', 'notes', 'phone', 'firstName', 'lastName', 'mobile']
    .map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})

    const queryParams: any = {
      filters: {
        $or: filteredProperties,
        $not: {
          markedForDeletion: true,
        },
      },
    }

    // add ignoreLimit if specified
    if(options?.ignoreLimit){
      queryParams.ignoreLimit = true;
    }

    const queryString = qs.stringify(queryParams,  {
      encodeValuesOnly: true, // prettify URL
    })
    
    return new Promise((resolve, reject)=>{
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/users-permissions/findRelatedUsers?${queryString}&populate=%2A`,
        headers: { 
          'Accept': 'application/json', 
        }
      };
      axios(config).then((response)=>{
        console.devtable(response.data);
        resolve(response.data);
      })
    .catch((error)=>{
      reject(error)
    })
  })
  }

  /**
   * Find the related user (patient of an accessible principal) with email
   */
  async findRelatedUserByEmail(email: string): Promise<{ documentId: string; firstName?: string; lastName?: string } | null> {
    const queryString = qs.stringify({ filters: { email: { $eqi: email.trim() } } }, { encodeValuesOnly: true });
    const response = await axios.get(`${environment.apiUrl}api/users-permissions/findRelatedUsers?${queryString}`, {
      headers: { Accept: 'application/json' },
    });
    return response.data?.data?.[0] ?? null;
  }

  /**
   *  Update user data
   */
  async updateUser(documentId: string, data: object) {
   return new Promise((resolve, reject) => { 

    var config = {
      method: 'put',
      maxBodyLength: Infinity,
      url: `${environment.apiUrl}api/users-permissions/editOwn/${documentId}`,
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': 'application/json', 
      },
      data : data
    };

    axios(config)
    .then(function (response) {
      console.devlog(JSON.stringify(response.data));
      resolve(response)
    })
    .catch(function (error) {
      reject(error)
      console.devlog(error);
    });

   });
  }

  /**
   * Mark user as deleted
   */
  markForDeletion(documentId: string) {
    return new Promise((resolve, reject) => {
      var config = {
        method: "delete",
        url: `${environment.apiUrl}api/users-permissions/mark-for-deletion/${documentId}`,
        headers: {
          Accept: "application/json",
        },
      };

      axios(config)
        .then(function (response: AxiosResponse) {
          console.devlog('mark for deletion:', response.data);
          resolve(response.data);
        })
        .catch(function (error) {
          reject(error);
        });
    });
  }

/**
*  Get find users via searchQueryOptions
*/ 

async findUsers(sqo: searchQueryOptions) {
   // build an array of objects with the properties to be searched
  let filteredProperties = ['username','email', 'matricule', 'notes', 'phone', 'firstName', 'lastName', 'mobile']
                              .map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
                              //e.g. [
  /*       {
          username: {
            $containsi: '2020-01-01',
          },
        },
        {
          date: {
            $containsi: '2020-01-02',
          },
        },
      ], */

  const queryString = qs.stringify({
    filters: {
      $or: filteredProperties,
      $not: {
        markedForDeletion: true,
      },
    },
  }, {
    encodeValuesOnly: true, // prettify URL
  });


  return new Promise((resolve, reject) => {
    
    var config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${environment.apiUrl}api/users?${queryString}&populate=%2A`,
      headers: { 
        'Accept': 'application/json', 
      }
    };
    axios(config)
    .then(function (response) {
      console.devtable(response.data);
      resolve(response.data);
      
    })
    .catch(function (error) {
      console.devlog(error);
      reject(error);
    });
    
  });

  }


    /**
   * Find the first name and last name using findMeta, leaving no/only certain logs
  */
 async findUserMetaFirstLastName(documentId: string) {
  return new Promise(async (resolve, reject) => {

    var config = {
      method: "get",
      url: `${environment.apiUrl}api/users-permissions/findMeta/?filters[id]=${documentId}`,
      headers: {
        Accept: "application/json",
       },
     };
     
     axios(config)
     .then(function (response: AxiosResponse) {
       console.devlog(response.data);
       response.data.results.length > 0 ? resolve(response.data.results.pop()) : resolve({});
       resolve(response.data.results.pop());
     })
     .catch(function (error) {
       reject(error);
     });
   });
 }
    /**
   * Get all the roles and details available
  */

 async getAllRoles() {
  return new Promise(async (resolve, reject) => {

    var config = {
      method: "get",
      url: `${environment.apiUrl}api/users-permissions/roles`,
      headers: {
        Accept: "application/json",
       },
     };
     
     axios(config)
     .then(function (response: AxiosResponse) {
       console.devlog("roles:" , response.data);
       response.data.roles.length > 0 ? resolve(response.data.roles) : resolve([]);
     })
     .catch(function (error) {
       reject(error);
     });
   });
 }

    /**
   * Get all the roles and details available
  */

 async getAllProfessionalRoles() {
  return new Promise(async (resolve, reject) => {

    var config = {
      method: "get",
      url: `${environment.apiUrl}api/definition-professional`,
      headers: {
        Accept: "application/json",
       },
     };
     
     axios(config)
     .then(function (response: AxiosResponse) {
       console.devlog("professional roles:" , response.data.data.roles);
       response.data.data.roles.length > 0 ? resolve(response.data.data.roles) : resolve([]);
     })
     .catch(function (error) {
      console.devlog("error fetching professional roles:", error);
       reject(error);
     });
   });
 }


  /**
   * update picture on any user 
   */
  async unsafeUpdatePictureOnAnyUser(documentId: string, image: File) {
    return new Promise((resolve, reject) => {

      var data = new FormData();
      data.append('ref', 'plugin::users-permissions.user');
      data.append('refId', documentId.toString());
      data.append('field', 'profilePicture');
      data.append('files', image, image.name);
      
      var config = {
        method: 'post',
      maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/upload/`,
        headers: { "Content-Type": "multipart/form-data" },
        /* headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json', 
          //@ts-ignore
          ...data.getHeaders()
        }, */
        data : data
      };
      
      axios(config)
      .then(function (response) {
        console.devlog("success !", response.data)
        resolve(response)
      })
      .catch(function (error) {
        console.devlog("error !", error)
        reject(error)
      });
      
    });

  }

    /**
   * upload document to any user 
   */
  async unsafeAddDocumentsOnAnyUser(documentId: string, documents: File[]) {

    return new Promise((resolve, reject) => {

      var data = new FormData();
      data.append('ref', 'plugin::users-permissions.user');
      data.append('refId', documentId.toString());
      data.append('field', 'documents');
      documents.forEach((doc) => {
        data.append('files', doc, doc.name);
      })
      
      var config = {
        method: 'post',
      maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/upload/`,
        headers: { "Content-Type": "multipart/form-data" },
        /* headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json', 
          //@ts-ignore
          ...data.getHeaders()
        }, */
        data : data
      };
      
      axios(config)
      .then(function (response) {
        console.devlog("success !", response.data)
        resolve(response)
      })
      .catch(function (error) {
        console.devlog("error !", error)
        reject(error)
      });
      
    });

  }

  /**
   * LEGACY Report the exporting of data records
   */
  async unsafeLogDataExporting(ids: number[], uid: string | number) {
    return new Promise((resolve, reject) => {

      var data =  { 
        data: {
          "affectedRecords": {
            "exported" : ids
          },
          "action": "export",
          "uid": uid,
          "timestamp": new Date(),
      }};
      
      var config = {
        method: 'post',
        url: `${environment.apiUrl}api/user-logs/`,
        headers: { 
          'Content-Type': 'application/json', 
        },
        data : data
      };
      
      axios(config)
      .then(function (response) {
        console.devlog("export log success !", response.data)
        resolve(response)
      })
      .catch(function (error) {
        console.devlog("export log error !", error)
        reject(error)
      });
      
    });

  }
  /**
   * Report the exporting of data records
   */
  async unsafeLogDataExportingByType(docType: docTypePlural, ids: number[], uid: string | number) {
    return new Promise((resolve, reject) => {

      var url : string;

      if (docType === 'users') {
        url = `${environment.apiUrl}api/user-logs/`
      } else if(docType === 'tarifs') {
        url = `${environment.apiUrl}api/tarif-logs/`
      } else if(docType === 'suffixes') {
        url = `${environment.apiUrl}api/suffix-logs/`
      } else if(docType === 'indices') {
        url = `${environment.apiUrl}api/index-logs/`
      } else {
        reject(new Error(`${docType} not implemented yet !`));
      }
    


      var data =  { 
        data: {
          "affectedRecords": {
            "exported" : ids
          },
          "action": "export",
          "uid": uid,
          "timestamp": new Date(),
      }};
      
      var config = {
        method: 'post',
        url: `${environment.apiUrl}api/user-logs/`,
        headers: { 
          'Content-Type': 'application/json', 
        },
        data : data
      };
      
      axios(config)
      .then(function (response) {
        console.devlog("export log success !", response.data)
        resolve(response)
      })
      .catch(function (error) {
        console.devlog("export log error !", error)
        reject(error)
      });
      
    });

  }

  
  async isAuthenticated(): Promise<boolean> {
    if (this.state.user) return true;

    if (this.setMeInFlight) {
      await this.setMeInFlight.catch(() => undefined);
      if (this.state.user) return true;
    }
    if (!this.hasStoredAuthToken()) return false;

    await this.setMe({ overridePublicRoutesCheck: true }).catch(() => undefined);
    return !!this.state.user;
  }


  private hasStoredAuthToken(): boolean {
    let valid = false;
    this.authService.getToken().subscribe((token) => {
      valid = !!token && token.isValid();
    });
    return valid;
  }

  getMyRole() {
    if (!this.state.user || !this.state.user.role || !this.state.user.role.type) {
      console.devlog("Role or user not found, fetching...");
      (async () => {
        await this.setMe()
        return this.state.getUser().role
      })()
    } else {
        return this.state.getUser().role
    }
  }

  async getMyRoleAsync() : Promise<{[key:string]: any, role: frontendRoleType}>{

    if (!this.state.user || !this.state.user.role || !this.state.user.role.type) {

      console.devlog("Role or user not found, fetching...");
        await this.setMe()

        return this.state.getUser().role
    } else {

        return this.state.getUser().role
    }
  }
  async getMyDetails() : Promise<Array<UserDetails[keyof UserDetails]>>{
    if (!this.state.user || !this.state.user.details) {
      console.devlog("User or user.details not found, fetching...");
        await this.setMe()
        return this.state.getUser().details
    } else {
        return this.state.getUser().details
    }
  }

  async getUserHistoryByVersion(documentId: string, version: number) {

    return new Promise(async (resolve, reject) => {
    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${environment.apiUrl}api/user-histories?filters[userID][$eq]=${documentId}&filters[version]=${version}&populate=%2A`,
      headers: { 
        'Accept': 'application/json',
      }
    };
    
    // due to filtering and/or populating, the populated data is returned like this:
    /* "editedBy": {
      "data": {
          "id": 49,
          "username": "manfredtest",
          "email": "authdebug1338@sharklasers.com" */

          //we shorten everything into this before resolving/returning the data:
            /* "editedBy": {
            "id": 49,
          "username": "manfredtest",
          "email": "authdebug1338@sharklasers.com" */

              try{

                let res = await axios.request(config)

                // strapi v5 migration: working directly on the data which should be flattened now
                // let id = res.data.data[0].editedBy.data.id
                //
                // res.data.data[0].editedBy = {... res.data.data[0].editedBy.data.attributes}
                // res.data.data[0].editedBy.id = id

                 let id = res.data.data[0].editedBy.data.id
                 res.data.data[0].editedBy = {... res.data.data[0].editedBy.data}
                 res.data.data[0].editedBy.id = id
                resolve(res.data);
              } catch(error) {
                reject(error)
              }
                
    /* .then((response) => {
      console.log(JSON.stringify(response.data));
      return JSON.stringify(response.data);
    })
    .catch((error) => {
      console.log(error);
      return error;
    }); */
    });
  }




    /**
   * Ask the backend for a tarif
   * @param documentId Tarif documentId
   * @param fetchFromMaster Fetch this record through the child from master. E.g. You <-> child instance <-> master instance
  */
 async getTarifById(documentId : string, fetchFromMaster) {
  return new Promise(async (resolve, reject) => {

 let theUrl = fetchFromMaster ? `${environment.apiUrl}api/tarifs/findOneFromMaster/${documentId}` : `${environment.apiUrl}api/tarifs/${documentId}?populate=%2A`;   

    var config = {
      method: "get",
      url: theUrl,
      headers: {
        Accept: "application/json",
       },
     };
     
     axios(config)
     .then(function (response: AxiosResponse) {
        console.devlog("getTarifById (but returning response.data.data)", response.data);
        resolve(response.data.data);
        })
     .catch(function (error) {
       reject(error);
     });
   });
 }

   /**
   *  Update tarif data
   */
   async updateTarif(documentId: string, data: object) {
    return new Promise((resolve, reject) => { 
 
     var config = {
       method: 'put',
       maxBodyLength: Infinity,
       url: `${environment.apiUrl}api/tarifs/${documentId}`,
       headers: { 
         'Content-Type': 'application/json', 
         'Accept': 'application/json', 
       },
       data : {data}
     };
 
     axios(config)
     .then(function (response) {
       console.devlog(JSON.stringify(response.data));
       resolve(response)
     })
     .catch(function (error) {
       reject(error)
       console.devlog(error);
     });
 
    });
   }

 /**
   * Mark tarif as deleted
   */
 markTarifForDeletion(documentId: string) {
  return new Promise((resolve, reject) => {
    var config = {
      method: "delete",
      url: `${environment.apiUrl}api/tarifs/mark-for-deletion/${documentId}`,
      headers: {
        Accept: "application/json",
      },
    };

    axios(config)
      .then(function (response: AxiosResponse) {
        console.devlog('mark for deletion:', response.data);
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      });
  });
}

async findTarifs(sqo: searchQueryOptions, filterOnlyCode: boolean = false, pageSize: number = 100, paginationPage : number = 1) : Promise<{results: any[], meta: any}> {
  // build an array of objects with the properties to be searched
 
  var filteredProperties = [];
  // case: we filter only by the tarif code
  if(filterOnlyCode) {
    filteredProperties = [  
      'code',
    ].map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
  } else {

    // case: we filter only by everything
     filteredProperties = [  
      'description',
      'code',
      'coefficient',
      'tariff',
      'nomenclatureID',
      'nomenclatureDescription',
      'partID',
      'partDescription',
      'chapterID',
      'chapterDescription',
      'sectionID',
      'sectionDescription',
      'subSectionID',
      'subSectionDescription',
      'subSubSectionDescription',
      'hide_from_children',
      'subSubSectionID',
      'number',
    ].map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
  }
    //e.g. [
 /*       {
         username: {
           $containsi: '2020-01-01',
         },
       },
       {
         date: {
           $containsi: '2020-01-02',
         },
       },
     ], */

 const queryString = qs.stringify({
   filters: {
     $or: filteredProperties,
     $not: {
       markedForDeletion: true,
     },
   },
 }, {
   encodeValuesOnly: true, // prettify URL
 });


 return new Promise((resolve, reject) => {
   
   var config = {
     method: 'get',
     maxBodyLength: Infinity,
     url: `${environment.apiUrl}api/tarifs?${queryString}&sort[0]=id&pagination[pageSize]=${pageSize}&pagination[page]=${paginationPage}&populate=%2A`,
     headers: { 
       'Accept': 'application/json', 
     }
   };
   axios(config)
   .then(function (response) {

    //  console.table(response.data.data);
    let resultsAndMeta = {results: response.data.data, meta: response.data.meta as strapiMeta}
     resolve(resultsAndMeta);
   }).catch(function (error) {
     console.devlog(error);
     reject(error);
   });
   
 });

 }

 async getTarifHistoryByVersion(documentId: string, version: number) {

  return new Promise(async (resolve, reject) => {
  let config = {
    method: 'get',
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/tarif-histories?filters[tarifId][$eq]=${documentId}&filters[version]=${version}&populate=%2A`,
    // url: `${environment.apiUrl}api/tarif-histories?filters[id][$eq]=1&filters[version]=0&populate=%2A`,
    headers: { 
      'Accept': 'application/json',
    }
  };
  
  // due to filtering and/or populating, the populated data is returned like this:
  /* "editedBy": {
    "data": {
        "id": 49,
        "attributes": {
            "username": "manfredtest",
            "email": "authdebug1338@sharklasers.com" */

            //we shorten everything into this before resolving/returning the data:
              /* "editedBy": {
             "id": 49,
            "username": "manfredtest",
            "email": "authdebug1338@sharklasers.com" */

            try{

              let res = await axios.request(config)

              // strapi v5 migration: working directly on the data which should be flattened now
              // let id = res.data.data[0].editedByUser.data.id
              //
              // res.data.data[0].editedByUser = {... res.data.data[0].editedByUser.data.attributes}
              // res.data.data[0].editedByUser.id = id

              let id = res.data.data[0].editedByUser.data.id
              res.data.data[0].editedByUser = {... res.data.data[0].editedByUser.data}
              res.data.data[0].editedByUser.id = id

              resolve(res.data);
            } catch(error) {
              reject(error)
            }
  });
}

async createNewTarif(data: any) {
  return new Promise((resolve, reject) => {

    var config = {
      method: 'post',
      url: `${environment.apiUrl}api/tarifs`,
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': 'application/json'
      },
      data : {data} 
    };
    
    axios(config)
    .then(function (response) {
      resolve(response.data)
    })
    .catch(function (error) {
      console.devlog("api create tarif error: ", error);
      reject(error)
    });
  });
}

async cloneTarif(documentId: string) : Promise<{documentId: string}> {
  return new Promise((resolve, reject) => {

    var config = {
      method: 'get',
      url: `${environment.apiUrl}api/tarifs/clone/${documentId}`,
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': 'application/json'
      },
    };
    
    axios(config)
    .then(function (response) {
      resolve(response.data)
    })
    .catch(function (error) {
      console.devlog("api create tarif error: ", error);
      reject(error)
    });
  });
}

async checkIfCodeExists(code: number) : Promise<{documentId: string, exists: boolean}> {
  return new Promise((resolve, reject) => {

    var config = {
      method: 'get',
      url: `${environment.apiUrl}api/tarifs/checkIfCodeExists/${code}`,
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': 'application/json'
      },
    };
    
    axios(config)
    .then(function (response) {
      resolve(response.data)
    })
    .catch(function (error) {
      console.devlog("api tarif checkIfCodeExists error: ", error);
      reject(error)
    });
  });
}



    /**
   * Ask the backend for a suffix
   * @param documentId suffix documentId
   * @param fetchFromMaster Fetch this record through the child from master. E.g. You <-> child instance <-> master instance
  */
    async getSuffixById(documentId : string, fetchFromMaster) {
      return new Promise(async (resolve, reject) => {
    
     let theUrl = fetchFromMaster ? `${environment.apiUrl}api/suffixes/findOneFromMaster/${documentId}` : `${environment.apiUrl}api/suffixes/${documentId}?populate=%2A`;
    
        var config = {
          method: "get",
          url: theUrl,
          headers: {
            Accept: "application/json",
           },
         };
         
         axios(config)
         .then(function (response: AxiosResponse) {
           console.devlog("getSuffixById (but returning response.data.data)", response.data);
    
           resolve(response.data.data)
            })
         .catch(function (error) {
           reject(error);
         });
       });
     }
    
       /**
       *  Update suffix data
       */
       async updateSuffix(documentId: string, data: object) {
        return new Promise((resolve, reject) => { 
     
         var config = {
           method: 'put',
           maxBodyLength: Infinity,
           url: `${environment.apiUrl}api/suffixes/${documentId}`,
           headers: { 
             'Content-Type': 'application/json', 
             'Accept': 'application/json', 
           },
           data : {data}
         };
     
         axios(config)
         .then(function (response) {
           console.devlog(JSON.stringify(response.data));
           resolve(response)
         })
         .catch(function (error) {
           reject(error)
           console.devlog(error);
         });
     
        });
       }
    
     /**
       * Mark suffix as deleted
       */
     markSuffixForDeletion(documentId: string) {
      return new Promise((resolve, reject) => {
        var config = {
          method: "delete",
          url: `${environment.apiUrl}api/suffixes/mark-for-deletion/${documentId}`,
          headers: {
            Accept: "application/json",
          },
        };
    
        axios(config)
          .then(function (response: AxiosResponse) {
            console.devlog('mark for deletion:', response.data);
            resolve(response.data);
          })
          .catch(function (error) {
            reject(error);
          });
      });
    }
    
    async findSuffixes(sqo: searchQueryOptions, filterOnlySuffix: boolean = false) {
      // build an array of objects with the properties to be searched
     
      var filteredProperties = [];
      // case: we filter only by the suffix
      if(filterOnlySuffix) {
        filteredProperties = [  
          'suffix',
        ].map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
      } else {
    
        // case: we filter only by everything
         filteredProperties = [  
          'coefficient',
          'suffix',
          'operand',
          'validFrom'
        ].map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
      }
        //e.g. [
     /*       {
             username: {
               $containsi: '2020-01-01',
             },
           },
           {
             date: {
               $containsi: '2020-01-02',
             },
           },
         ], */
    
     const queryString = qs.stringify({
       filters: {
         $or: filteredProperties,
         $not: {
           markedForDeletion: true,
         },
       },
     }, {
       encodeValuesOnly: true, // prettify URL
     });
    
    
     return new Promise((resolve, reject) => {
       
       var config = {
         method: 'get',
         maxBodyLength: Infinity,
         url: `${environment.apiUrl}api/suffixes?${queryString}&sort[0]=id&pagination[pageSize]=100`,
         headers: { 
           'Accept': 'application/json', 
         }
       };
       axios(config)
       .then(function (response) {
    
        //  console.table(response.data.data);    
         resolve(response.data.data);
         
       })
       .catch(function (error) {
         console.devlog(error);
         reject(error);
       });
       
     });
    
     }
    
     async getSuffixHistoryByVersion(documentId: string, version: number) {
    
      return new Promise(async (resolve, reject) => {
      let config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${environment.apiUrl}api/suffix-histories?filters[suffixId][$eq]=${documentId}&filters[version]=${version}&populate=%2A`,
        // url: `${environment.apiUrl}api/suffix-histories?filters[id][$eq]=1&filters[version]=0&populate=%2A`,
        headers: { 
          'Accept': 'application/json',
        }
      };
      
      // due to filtering and/or populating, the populated data is returned like this:
      /* "editedBy": {
        "data": {
            "id": 49,
            "attributes": {
                "username": "manfredtest",
                "email": "authdebug1338@sharklasers.com" */
    
                //we shorten everything into this before resolving/returning the data:
                  /* "editedBy": {
                 "id": 49,
                "username": "manfredtest",
                "email": "authdebug1338@sharklasers.com" */
    
                try{

                  let res = await axios.request(config)

                  // strapi v5 migration: working directly on the data which should be flattened now
                  // let id = res.data.data[0].editedByUser.data.id
                  //
                  // res.data.data[0].editedByUser = {... res.data.data[0].editedByUser.data.attributes}
                  // res.data.data[0].editedByUser.id = id

                  let id = res.data.data[0].editedByUser.data.id
                  res.data.data[0].editedByUser = {... res.data.data[0].editedByUser.data}
                  res.data.data[0].editedByUser.id = id

                  resolve(res.data);
                } catch(error) {
                  reject(error)
                }
      });
    }
    
    async createNewSuffix(data: any) {
      return new Promise((resolve, reject) => {
    
        var config = {
          method: 'post',
          url: `${environment.apiUrl}api/suffixes`,
          headers: { 
            'Content-Type': 'application/json', 
            'Accept': 'application/json'
          },
          data : {data} 
        };
        
        axios(config)
        .then(function (response) {
          resolve(response.data)
        })
        .catch(function (error) {
          console.devlog("api create suffix error: ", error);
          reject(error)
        });
      });
    }
    
    async cloneSuffix(documentId: string) : Promise<{documentId: string}> {
      return new Promise((resolve, reject) => {
    
        var config = {
          method: 'get',
          url: `${environment.apiUrl}api/suffixes/clone/${documentId}`,
          headers: { 
            'Content-Type': 'application/json', 
            'Accept': 'application/json'
          },
        };
        
        axios(config)
        .then(function (response) {
          resolve(response.data)
        })
        .catch(function (error) {
          console.devlog("api create suffix error: ", error);
          reject(error)
        });
      });
    }
    
    async checkIfSuffixWithDateExists(suffix: string, validFrom: string) : Promise<{documentId: string, exists: boolean}> {
      return new Promise((resolve, reject) => {
    
        var config = {
          method: 'get',
          url: `${environment.apiUrl}api/suffixes/checkIfSuffixWithDateExists/?suffix=${suffix}&validFrom=${validFrom}`,
          headers: { 
            'Content-Type': 'application/json', 
            'Accept': 'application/json'
          },
        };
        
        axios(config)
        .then(function (response) {
          resolve(response.data)
        })
        .catch(function (error) {
          console.devlog("api suffix checkIfSuffixExists error: ", error);
          reject(error)
        });
      });
    }
    


        /**
   * Ask the backend for an index
   * @param documentId index documentId
   * @param fetchFromMaster Fetch this record through the child from master. E.g. You <-> child instance <-> master instance
  */
        async getIndexById(documentId : string, fetchFromMaster) {
          return new Promise(async (resolve, reject) => {
        
         let theUrl = fetchFromMaster ? `${environment.apiUrl}api/indices/findOneFromMaster/${documentId}` : `${environment.apiUrl}api/indices/${documentId}?populate=%2A`;   
        
            var config = {
              method: "get",
              url: theUrl,
              headers: {
                Accept: "application/json",
               },
             };
             
             axios(config)
             .then(function (response: AxiosResponse) {
               console.devlog("getIndexById (but returning response.data.data)", response.data);
                  resolve(response.data.data);
                })
             .catch(function (error) {
               reject(error);
             });
           });
         }
        
           /**
           *  Update index data
           */
           async updateIndex(documentId: string, data: object) {
            return new Promise((resolve, reject) => { 
         
             var config = {
               method: 'put',
               maxBodyLength: Infinity,
               url: `${environment.apiUrl}api/indices/${documentId}`,
               headers: { 
                 'Content-Type': 'application/json', 
                 'Accept': 'application/json', 
               },
               data : {data}
             };
         
             axios(config)
             .then(function (response) {
               console.devlog(JSON.stringify(response.data));
               resolve(response)
             })
             .catch(function (error) {
               reject(error)
               console.devlog(error);
             });
         
            });
           }
        
         /**
           * Mark index as deleted
           */
         markIndexForDeletion(documentId: string) {
          return new Promise((resolve, reject) => {
            var config = {
              method: "delete",
              url: `${environment.apiUrl}api/indices/mark-for-deletion/${documentId}`,
              headers: {
                Accept: "application/json",
              },
            };
        
            axios(config)
              .then(function (response: AxiosResponse) {
                console.devlog('mark for deletion:', response.data);
                resolve(response.data);
              })
              .catch(function (error) {
                reject(error);
              });
          });
        }
        
        async findIndices(sqo: searchQueryOptions, filterOnlyIndex: boolean = false) {
          // build an array of objects with the properties to be searched
         
          var filteredProperties = [];
          // case: we filter only by the index
          if(filterOnlyIndex) {
            filteredProperties = [  
              'nomenclatureID',
            ].map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
          } else {
        
            // case: we filter only by everything
             filteredProperties = [  
              'coefficient',
              "nomenclatureID",
              'validFrom'
            ].map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
          }
            //e.g. [
         /*       {
                 username: {
                   $containsi: '2020-01-01',
                 },
               },
               {
                 date: {
                   $containsi: '2020-01-02',
                 },
               },
             ], */
        
         const queryString = qs.stringify({
           filters: {
             $or: filteredProperties,
             $not: {
               markedForDeletion: true,
             },
           },
         }, {
           encodeValuesOnly: true, // prettify URL
         });
        
        
         return new Promise((resolve, reject) => {
           
           var config = {
             method: 'get',
             maxBodyLength: Infinity,
             url: `${environment.apiUrl}api/indices?${queryString}&sort[0]=id&pagination[pageSize]=100`,
             headers: { 
               'Accept': 'application/json', 
             }
           };
           axios(config)
           .then(function (response) {
        
            //  console.table(response.data.data);        
             resolve(response.data.data);
             
           })
           .catch(function (error) {
             console.devlog(error);
             reject(error);
           });
           
         });
        
         }
        
         async getIndexHistoryByVersion(documentId: string, version: number) {
        
          return new Promise(async (resolve, reject) => {
          let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${environment.apiUrl}api/index-histories?filters[indexId][$eq]=${documentId}&filters[version]=${version}&populate=%2A`,
            // url: `${environment.apiUrl}api/index-histories?filters[id][$eq]=1&filters[version]=0&populate=%2A`,
            headers: { 
              'Accept': 'application/json',
            }
          };
          
          // due to filtering and/or populating, the populated data is returned like this:
          /* "editedBy": {
            "data": {
                "id": 49,
                "attributes": {
                    "username": "manfredtest",
                    "email": "authdebug1338@sharklasers.com" */
        
                    //we shorten everything into this before resolving/returning the data:
                      /* "editedBy": {
                     "id": 49,
                    "username": "manfredtest",
                    "email": "authdebug1338@sharklasers.com" */
        
                    try{

                      let res = await axios.request(config)

                      // strapi v5 migration: working directly on the data which should be flattened now
                      // let id = res.data.data[0].editedByUser.data.id
                      //
                      // res.data.data[0].editedByUser = {... res.data.data[0].editedByUser.data.attributes}
                      // res.data.data[0].editedByUser.id = id

                      let id = res.data.data[0].editedByUser.data.id
                      res.data.data[0].editedByUser = {... res.data.data[0].editedByUser.data}
                      res.data.data[0].editedByUser.id = id

                      resolve(res.data);
                    } catch(error) {
                      reject(error)
                    }
          });
        }
        
        async createNewIndex(data: any) {
          return new Promise((resolve, reject) => {
        
            var config = {
              method: 'post',
              url: `${environment.apiUrl}api/indices`,
              headers: { 
                'Content-Type': 'application/json', 
                'Accept': 'application/json'
              },
              data : {data} 
            };
            
            axios(config)
            .then(function (response) {
              resolve(response.data)
            })
            .catch(function (error) {
              console.devlog("api create index error: ", error);
              reject(error)
            });
          });
        }
        
        async cloneIndex(documentId: string) : Promise<{documentId:string}> {
          return new Promise((resolve, reject) => {
        
            var config = {
              method: 'get',
              url: `${environment.apiUrl}api/indices/clone/${documentId}`,
              headers: { 
                'Content-Type': 'application/json', 
                'Accept': 'application/json'
              },
            };
            
            axios(config)
            .then(function (response) {
              resolve({documentId: response.data.documentId})
            })
            .catch(function (error) {
              console.devlog("api create index error: ", error);
              reject(error)
            });
          });
        }
        
        async checkIfIndexWithDateExists(nomenclatureID: string, validFrom: string) : Promise<{documentId:string, exists: boolean}> {
          return new Promise((resolve, reject) => {
        
            var config = {
              method: 'get',
              url: `${environment.apiUrl}api/indices/checkIfIndexWithDateExists/?nomenclatureID=${nomenclatureID}&validFrom=${validFrom}`,
              headers: { 
                'Content-Type': 'application/json', 
                'Accept': 'application/json'
              },
            };
            
            axios(config)
            .then(function (response) {
              resolve(response.data)
            })
            .catch(function (error) {
              console.devlog("api index checkIfCodeExists error: ", error);
              reject(error)
            });
          });
        }
        
             
        async findCurrentlyApplicableIndexByNomenclatureID(nomenclatureID: boolean = false) {
          // build an array of objects with the properties to be searched
        
        let today = new Date().toISOString().slice(0, 10) // e.g. '2024-04-18'
        let fullUrl = `${environment.apiUrl}api/indices?filters[nomenclatureID][$eqi]=${nomenclatureID}&sort=validFrom:DESC&filters[validFrom][$lt]=${today}&pagination[limit]=1`

         return new Promise((resolve, reject) => {
           
           var config = {
             method: 'get',
             maxBodyLength: Infinity,
             url: fullUrl,
             headers: { 
               'Accept': 'application/json', 
             }
           };
           axios(config)
          .then(function (response) {
            //  console.table(response.data.data);        
             resolve(response.data.data);
           })
           .catch(function (error) {
             console.devlog(error);
             reject(error);
           });
           
         });
        
         }
//TODO: Add usual search query option parameters etc !
         async getAllTarifNomenclatures() {
            return new Promise<{ [key:number] : string }>((resolve, reject) => {
              const queryString = qs.stringify({
                filters: {
                  $not: {
                    markedForDeletion: true,
                  },
                },
              });

              let config = {
                method: 'get',
                maxBodyLength: Infinity,
                url: `${environment.apiUrl}api/tarif-nomenclatures/`,
                headers: { 
                  'Accept': 'application/json', 
                }
              };
              
              axios.request(config)
              .then((response) => {

                /* 
                // we want this format: {
                  13 : "Orthophonistes",
                  30 : "Infirmiers",
                  31 : "Sages-femmes"} */

                  // strapi v5 migration:
                /*   let flattened = response.data.data.map(obj => {
                    // Merge attributes into the original object
                    const mergedObj = { ...obj, ...obj.attributes };
                    // Remove the 'attributes' property from the merged object
                    delete mergedObj.attributes;
                    return mergedObj;
                }); */

                let formattedData = {}
                // strapi v5 migration: working directly on the data which should be flattened now
                response.data.data.forEach((nomenclature) => {
                  formattedData[nomenclature.nomenclatureID] = nomenclature.name
                })
                 
                resolve(formattedData);
              })
              .catch((error) => {
                reject(error);
              });
              

         })
        }
         async getAllNomenclatureIDsAndNamesDistinctFromExistingIndexes(includeMaster: boolean = false) {
            return new Promise<{ [key:number] : string }>((resolve, reject) => {
              const queryString = qs.stringify({
                filters: {
                  $not: {
                    markedForDeletion: true,
                  },
                },
              });

              let finalUrl = includeMaster ? `${environment.apiUrl}api/indices/allNomenclaturesDistinct/?includeMaster=true` : `${environment.apiUrl}api/indices/allNomenclaturesDistinct/`
              let config = {
                method: 'get',
                maxBodyLength: Infinity,
                url: finalUrl,
                headers: { 
                  'Accept': 'application/json', 
                }
              };
              
              axios.request(config)
              .then((response) => {

                //  the formatting now takes place on the server !

                // let formattedData = {}
                /* 
                // we want this format: {
                  13 : "Orthophonistes",
                  30 : "Infirmiers",
                  31 : "Sages-femmes"} */

                 /*  response.data.forEach((nomenclature) => {
                    if(nomenclature.nomenclature_description) {
                      formattedData[nomenclature.nomenclature_id] = nomenclature.nomenclature_description
                      }
                  }) */

                resolve(response.data);
              })
              .catch((error) => {
                reject(error);
              });
              

         })
        }

        // ADAPT TO GETTING FROM INDEX API Endpoint
        async getNomenclatureNameByNomenclatureID(nomenclatureID: number) {
          return new Promise((resolve, reject) => {
            
            let config = {
              method: 'get',
              maxBodyLength: Infinity,
              url: `${environment.apiUrl}api/tarif-nomenclatures?filters[nomenclatureID][$eqi]=${nomenclatureID}`,
              headers: { 
                'Accept': 'application/json', 
              }
            };
            
            axios.request(config)
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              reject(error);
            });
            

       })
      }

      async changePassword({currentPassword, password, passwordConfirmation}){

        return new Promise((resolve, reject) => {


            let data = {
              "currentPassword": currentPassword,
              "password": password,
              "passwordConfirmation": passwordConfirmation
            };

            let config = {
              method: 'post',
              maxBodyLength: Infinity,
              url: `${environment.apiUrl}api/auth/change-password`,
              headers: { 
                'Accept': 'application/json', 
              },
              data : data
            };

            axios.request(config)
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              reject(error);
            });

       });
      }

      async searchPlaces(input: string, searchSessionToken: string) : Promise<GcloudLocationSearchResponse> {
        return new Promise((resolve, reject) => {

          let language = localStorage.getItem('language');
          if(!language) {
            language = 'en'
          }

          let data = JSON.stringify({
            "input": input,
            "includeQueryPredictions": "true",
            "languageCode": language,
            "locationBias": {
              "circle": {
                "center": {
                  "latitude": "49.8152995",
                  "longitude": "6.13332"
                },
                "radius": "1400.0"
              }
            },
            "sessionToken": searchSessionToken
          });
          
          let config = {
            skipInterceptor: true,
            method: 'post',
            maxBodyLength: Infinity,
            url: 'https://places.googleapis.com/v1/places:autocomplete',
            headers: { 
              'X-Goog-Api-Key': environment.gcloudMapsPublicApiKey, 
              'X-Goog-FieldMask': '*', 
              'Content-Type': 'application/json'
            },
            data : data
          };
          
          axios.request(config)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
          

        })
      }

      async getPlaceDetails(placeID: string, searchSessionToken: string) : Promise<{ message: GcloudLocationDetailResponse }> {
        return new Promise((resolve, reject) => {

        let config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${environment.apiUrl}api/users-permissions/place-details/?place_id=${placeID}&sessionToken=${searchSessionToken}`,
        };

        axios.request(config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });


        })
      }
      

      async getGPSforAddress(arbitraryAddressString : string) : Promise<any> {
        return new Promise((resolve, reject) => {
                   
         /*  let config = {
            skipInterceptor: true,
            method: 'get',
            maxBodyLength: Infinity,
            url: `'https://maps.googleapis.com/maps/api/geocode/json?address=${arbitraryAddressString}&key=${environment.gcloudMapsPublicApiKey}`,
            headers: { 
              'X-Goog-FieldMask': '*', 
              'Content-Type': 'application/json',
            },
          };
           */

          let config = {
            method: 'get',
            url: `${environment.apiUrl}api/users-permissions/get-GPS/?arbitraryAddressString=${arbitraryAddressString}`,
            /* headers: { 
              'X-Goog-FieldMask': 'places.formattedAddress'
            } */
          };

          
          axios.request(config)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
          

        })
      }
        
       /** * Check the validity of a P12 certificate
       * @param cnsCode The CNS code of the user
       * @param password The password for the P12 certificate
       * @param certificate The P12 certificate file
       */
      async checkp12CertValidity(cnsCode : string, password : string, certificate: File) : Promise<AxiosResponse>{
        return new Promise((resolve, reject) => {
         
          console.devwarn("Using master as API endpoint for P12 certificate validity check. This is for development only, use the right HH backend !");

          let data = new FormData();
          data.append('password', password);
          data.append('cnsCode', cnsCode);
          data.append('certificate', certificate);
          let config = {
            skipInterceptor: true,
            method: 'post',
            maxBodyLength: Infinity,
            url: `${environment.apiUrl}api/users-permissions/checkP12CertValidity/`,
            headers: { "Content-Type": "multipart/form-data" },
            data : data
          };
          
          axios.request(config)
          .then((response) => {
            console.devlog(JSON.stringify(response.data));
            resolve(response);
          })
          .catch((error) => {
            console.devlog(error);
            reject(error);
          });
          
        })
      }


       /** * Register or log in with a valid P12 certificate
       * @param cnsCode The CNS code of the user
       * @param password The password for the P12 certificate
       * @param certificate The P12 certificate file
       */
      async RegisterOrLoginWithP12(cnsCode : string, password : string, userPassword: string, certificate: File) : 
       Promise<AxiosResponse<RegisterOrLoginWithP12["ResponseData"]["Success"] | RegisterOrLoginWithP12["ResponseData"]["BadRequest"] | RegisterOrLoginWithP12["ResponseData"]["Error"]>>{
        return new Promise((resolve, reject) => {
         
          console.devwarn("Using master as API endpoint for P12 certificate validity check. This is for development only, use the right HH backend !");
          environment.apiUrl = environment.apiUrlMaster
          console.devlog("API URL set to:", environment.apiUrl);
          let data = new FormData();
          data.append('password', password);
          data.append('cnsCode', cnsCode);
          data.append('userPassword', userPassword);
          data.append('certificate', certificate);
          let config = {
            skipInterceptor: true,
            method: 'post',
            maxBodyLength: Infinity,
            url: `${environment.apiUrl}api/hhportal/RegisterOrLoginWithP12/`,
            headers: { "Content-Type": "multipart/form-data" },
            data : data
          };
          
          axios.request(config)
          .then((response) => {
            console.devlog(JSON.stringify(response.data));
            resolve(response);
          })
          .catch((error) => {
            console.devlog(error);
            reject(error);
          });
          
        })
      }

      async createEvent(event : EventCreateData) : Promise<AxiosResponse<any>> {
          return new Promise((resolve, reject) => {

            let data = JSON.stringify({
              "data": event
            });

            let config = {
              method: 'post',
              maxBodyLength: Infinity,
              headers: { "Content-Type": "application/json" },
              url: `${environment.apiUrl}api/events`,
              data : data
            };
            axios.request(config)
            .then((response) => {
              resolve(response);
              console.devlog(response);
            })
            .catch((error) => {
              console.devlog(error);
              reject(error);
            });
          })
      }

      // TODO: add from/to (time) parameter
      async findEvents(sqo?: searchQueryOptions) : Promise<any> {
          return new Promise((resolve, reject) => {
            var config 
            if(sqo){
            // build an array of objects with the properties to be searched
            let filteredProperties = ['title' ,'description', 'dateTime', 'practitioner', 'client']
                                        .map((prop) => { let o = new Object(); o[prop] = {$containsi: sqo._contains}; return o;})
                                        //e.g. [
            /*       {
                    username: {
                      $containsi: '2020-01-01',
                    },
                  },
                  {
                    date: {
                      $containsi: '2020-01-02',
                    },
                  },
                ], */

              const queryString = qs.stringify({
                filters: {
                  $or: filteredProperties,
                  $not: {
                    markedForDeletion: true,
                  },
                },
              }, {
                encodeValuesOnly: true, // prettify URL
              });
              
              
              
                config = {
                  method: 'get',
                  maxBodyLength: Infinity,
                  url: `${environment.apiUrl}api/events?${queryString}&populate[services][populate]=*&populate[practitioner]=*&populate[client]=*`,
                  
                };
              } else {
                config = {
                  method: 'get',
                  maxBodyLength: Infinity,
                  url: `${environment.apiUrl}api/events?populate[services][populate]=*&populate[practitioner]=*&populate[client]=*`,
                };
              }

            axios.request(config)
            .then((response) => {
              resolve(response);
              // console.log(JSON.stringify(response.data));
            })
            .catch((error) => {
              console.devlog(error);
              reject(error);
            });
          })
      }

      async updateEvent(documentId: string, data: EventUpdateData) : Promise<any> {
          return new Promise((resolve, reject) => {
            let config = {
              method: 'put',
              url: `${environment.apiUrl}api/events/${documentId}`,
              headers: { 
                'Content-Type': 'application/json', 
              },
              data : JSON.stringify({ data: data })
            };

            console.devlog("config",config)

            axios.request(config)
            .then((response) => {
              resolve(response.data);
              console.devlog(JSON.stringify(response.data));
            })
            .catch((error) => {
              console.devlog(error);
              reject(error);
            });
          })
      }

      async deleteEvent(documentId: string) : Promise<any> {
        return new Promise((resolve, reject) => {
          let config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${environment.apiUrl}api/events/${documentId}`,
          };

          axios.request(config)
          .then((response) => {
            console.devlog(JSON.stringify(response.data));
            resolve(response.data);
          })
          .catch((error) => {
            console.devlog(error);
            reject(error);
          });
        })
       }

      async forgotPassword(email: string){
        return new Promise( async (resolve, reject)=>{

          const normalizedEmail = this.deepTrimStrings(email);

          let config = {
            method: "post",
            skipInterceptor: true,
            url: `${environment.apiUrl}api/users/forgot-password`,
            headers: {
              Accept: "application/json",
            },
            data: {
              "email": normalizedEmail
            }
          }
          axios(config)
          .then((response)=>{
            resolve(response)
          }).catch((error)=>{
            reject(error)
            console.deverror("Error in forgot password:", error)
          })
        })

      }

      async resetPassword(code: string, password: string, passwordConfirmation: string){
        return new Promise( async (resolve, reject)=>{

          var config = {
            method: "post",
            skipInterceptor: true,
            url: `${environment.apiUrl}api/users/reset-password`,
            headers: {
              Accept: "application/json",
            },
            data: {
              "code": code,
              "password": password,
              "passwordConfirmation": passwordConfirmation
            }
          }
          axios.request(config)
          .then((response)=>{
            resolve(response.data)
          }).catch((error)=>{
            reject(error)
            console.deverror("Error in reset password:", error)
          })
        })

      }

      async setUserLanguage(language: availableLanguages): Promise<any> {
        return new Promise((resolve, reject) => {
          axios({
            method: 'put',
            url: `${environment.apiUrl}api/users-permissions/setLanguage`,
            headers: { Accept: 'application/json' },
            data: { language },
          })
            .then(response => resolve(response.data))
            .catch(error => reject(error));
        });
      }
      

      // Admin/assistant: trigger a password reset email for a specific user by ID
      async triggerPasswordResetForUser(userId: number){
        return new Promise((resolve, reject) => {
          const config = {
            method: 'post',
            url: `${environment.apiUrl}api/users/trigger-password-reset`,
            headers: {
              Accept: 'application/json',
            },
            data: { userId }
          };
          axios.request(config)
            .then((response) => resolve(response.data))
            .catch((error) => {
              console.deverror('Error triggering password reset:', error);
              reject(error);
            });
        });
      }

      async loadAllowedPrincipals(): Promise<Array<PrincipalInfo>> {
        try {
          const response = await this.getAllowedPrincipals();
          const principals = Array.isArray(response?.data?.data) ? response.data.data : response.data.data ? [response.data.data] : [];
          return principals;
        } catch (error) {
          console.deverror('Error loading allowed principals:', error);
          return [];
        }
      }


    

       // Professional Groups:
       createProfessionalGroup = import_createProfessionalGroup;
       myGroupsAndInvites = import_myGroupsAndInvites;
        inviteToProfessionalGroup = import_inviteToProfessionalGroup;
       acceptInviteToProfessionalGroup = import_acceptInviteToProfessionalGroup;
       leaveOrRemoveFromGroup = import_leaveOrRemoveFromGroup;

      //  User Relations 
      myDirectEmployees = import_myDirectEmployees;
      deleteRelation = import_deleteRelation;


       // User Groups:
       inviteUserWithCode = import_inviteUserWithCode;
       registerUser = import_registerUser;
       acceptInviteUserWithCode = import_acceptInviteUserWithCode;
       getMyUserInvites = import_getMyUserInvites;
       respondToUserInvite = import_respondToUserInvite;
       registerPaymentCard = import_registerPaymentCard;


           // Payment: 
       createProfessionalPaymentAccount = import_createProfessionalPaymentAccount;
       checkVerificationStatus = import_checkVerificationStatus;
       chargePatient = import_chargePatient;
       deletepaymentCard = import_deletePaymentCard;
       setDefaultPaymentCard = import_setDefaultPaymentCard;
       getMyCards = import_getMyCards;
       createPaymentLink  = import_createPaymentLink;
       getPaymentLinks = import_getPaymentLinks;
       resendPaymentLink = import_resendPaymentLink;
       

       
      getAllowedPrincipals = import_getAllowedPrincipals
       

       // User Groups:
      sendProfessionalRegistrationInvite = import_sendProfessionalRegistrationInvite;
      getMyTeams = import_getMyTeams
      inviteEmployee = import_inviteEmployee;
      getMyEmployeeInvites = import_getMyEmployeeInvites;
      acceptEmployeeInvite = import_acceptEmployeeInvite;

       //Esante
       authenticateWithOidc = import_authenticateWithOidc;

      // forms
      findPdfForms = import_findPdfForms;
      loadPdfFormSchema = import_loadPdfFormSchema;
      findLatestFormSubmission = import_findLatestFormSubmission;
      saveFormSubmission = import_saveFormSubmission;

      //favourite icd10 codes
      getMyFavouriteIcd10Codes = import_getMyFavouriteIcd10Codes;
      updateMyFavouriteIcd10Codes = import_updateMyFavouriteIcd10Codes;
      
      ecit = import_ecit;
      getOIDCstatusAndExtend = import_getOIDCstatusAndExtend;
      getMyEcits = import_getMyEcits;

      ngOnDestroy(): void {
           this.destroy$.next();
           this.destroy$.complete();
      }

      }

export type EventCreateData = {
  practitioner: number;
  client: number;
  services: Array<{ quantity: number; customDate: string | null, tarif: number }>;
  start: string; // ISO string
  end: string;   // ISO string
  title: string;
  description: string;
};

export type EventUpdateData = {
  practitioner?: number;
  client?: number;
  services?: Array<{ documentId: string;
                     quantity?: number; 
                     customDate?: string | null;
                     tarif?: {
                      data : {
                        documentId: string;
                      }
                     } 
                   }>;
  start?: string; //ISO string
  end?: string; //ISO string
  title?: string;
  description?: string;
}
